"use client";

import { useQuery } from "@tanstack/react-query";
import { AlertTriangle } from "@untitledui/icons";
import { useEffect, useRef, useState } from "react";

import { capturePosthogEvent } from "@/lib/posthog-browser";
import { cx } from "@/utils/cx";

export type BillingPastDueDunningInfo = {
	attemptCount: number;
	phase: string;
	gracePeriodEndsAtEt: string | null;
};

type BillingSubscriptionResponse = {
	subscription: {
		status: string;
	} | null;
	dunning: BillingPastDueDunningInfo | null;
};

export type BillingPastDueBannerViewProps = {
	dunning: BillingPastDueDunningInfo | null;
	onManageBilling: () => void;
	portalLoading?: boolean;
	portalError?: string | null;
	/** Soften sticky/full-bleed chrome when embedded in a preview dialog. */
	embedded?: boolean;
};

export function BillingPastDueBannerView({
	dunning,
	onManageBilling,
	portalLoading = false,
	portalError = null,
	embedded = false,
}: BillingPastDueBannerViewProps) {
	const isFinalNotice = dunning && (dunning.attemptCount >= 4 || dunning.phase === "grace_period");

	return (
		<div
			className={cx(
				"w-full border-b border-warning-300 bg-warning-50 px-4 py-3 dark:border-warning-800 dark:bg-warning-950",
				embedded ? "rounded-lg border" : "sticky top-0 z-50",
			)}
		>
			<div
				className={cx(
					"flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between",
					!embedded && "page-shell",
				)}
			>
				<div className="flex items-start gap-2 sm:items-center">
					<AlertTriangle className="mt-0.5 size-5 shrink-0 text-warning-600 sm:mt-0 dark:text-warning-400" />
					<p className="text-sm font-medium text-warning-800 dark:text-warning-200">
						<span className="font-semibold">Payment failed.</span>{" "}
						{isFinalNotice && dunning?.gracePeriodEndsAtEt ? (
							<>
								Final notice — update your payment method by {dunning.gracePeriodEndsAtEt} to keep
								Pro access.
							</>
						) : (
							<>Update your payment method to keep your Pro membership.</>
						)}
					</p>
				</div>
				<div className="flex shrink-0 flex-col items-stretch gap-1 sm:items-end">
					<button
						type="button"
						onClick={onManageBilling}
						disabled={portalLoading}
						className="rounded-md bg-warning-700 px-3 py-1.5 text-sm font-semibold text-white hover:bg-warning-800 disabled:opacity-60 dark:bg-warning-600 dark:hover:bg-warning-500"
					>
						{portalLoading ? "Opening…" : "Manage billing & payment methods"}
					</button>
					{portalError ? (
						<p className="text-xs text-error-primary" role="alert">
							{portalError}
						</p>
					) : null}
				</div>
			</div>
		</div>
	);
}

export function BillingPastDueBanner() {
	const [portalLoading, setPortalLoading] = useState(false);
	const [portalError, setPortalError] = useState<string | null>(null);

	const { data } = useQuery({
		queryKey: ["billing", "subscription", "banner"],
		queryFn: async (): Promise<BillingSubscriptionResponse> => {
			const res = await fetch("/api/billing/subscription", { credentials: "include" });
			if (!res.ok) throw new Error("Failed to load billing status");
			return res.json();
		},
		staleTime: 60_000,
		refetchOnWindowFocus: true,
	});

	const sub = data?.subscription;
	const dunning = data?.dunning ?? null;
	const isPastDue = sub?.status === "past_due";
	const impressionSentRef = useRef(false);

	useEffect(() => {
		if (!isPastDue || impressionSentRef.current) return;
		impressionSentRef.current = true;
		void capturePosthogEvent("past_due_banner_shown", {
			attempt_count: dunning?.attemptCount ?? null,
			phase: dunning?.phase ?? null,
		});
	}, [isPastDue, dunning?.attemptCount, dunning?.phase]);

	if (!isPastDue) {
		return null;
	}

	async function openPortal() {
		setPortalError(null);
		setPortalLoading(true);
		try {
			const res = await fetch("/api/billing/portal-session", {
				method: "POST",
				credentials: "include",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ source: "past_due_banner" }),
			});
			const payload = (await res.json().catch(() => ({}))) as { url?: string; error?: string };
			if (!res.ok || !payload.url) {
				throw new Error(payload.error || "Could not open billing portal.");
			}
			void capturePosthogEvent("billing_portal_opened", { source: "past_due_banner" });
			window.location.href = payload.url;
		} catch (e) {
			setPortalError(e instanceof Error ? e.message : "Could not open billing portal.");
		} finally {
			setPortalLoading(false);
		}
	}

	return (
		<BillingPastDueBannerView
			dunning={dunning}
			onManageBilling={() => void openPortal()}
			portalLoading={portalLoading}
			portalError={portalError}
		/>
	);
}
